<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
  <div v-if="items != null">
    <EditDialog
      v-model="editDialog"
      :save-button-text="itemId === 'new' ? $t('create') : $t('save')"
      :title="itemId === 'new' ? $t('newEEBuild') : $t('editEEBuild')"
      :max-width="800"
      :transition="false"
      @save="loadItems"
    >
      <template v-slot:form="{ onSave, onError, needSave, needReset }">
        <EEBuildForm
          :project-id="projectId"
          :item-id="itemId"
          :need-save="needSave"
          :need-reset="needReset"
          @save="onSave"
          @error="onError"
        />
      </template>
    </EditDialog>

    <YesNoDialog
      :title="$t('deleteEEBuild')"
      :text="$t('askDeleteEEBuild')"
      v-model="deleteItemDialog"
      @yes="deleteItem(itemId)"
    />

    <v-dialog v-model="logDialog" max-width="900" scrollable>
      <v-card>
        <v-card-title>{{ $t('eeBuildLog') }}</v-card-title>
        <v-card-text>
          <pre
              style="font-family: 'Commit Mono', ui-monospace, monospace;
                     font-size: 12px;
                     white-space: pre-wrap;"
          >{{ logText || $t('eeBuildNoLog') }}</pre>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="logDialog = false">{{ $t('close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-toolbar flat>
      <v-app-bar-nav-icon @click="showDrawer()"></v-app-bar-nav-icon>
      <v-toolbar-title>{{ $t('eeBuilds') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn color="primary" @click="editItem('new')">{{ $t('newEEBuild') }}</v-btn>
    </v-toolbar>

    <v-divider />

    <v-data-table
      :headers="headers"
      :items="items"
      class="mt-4"
      :no-data-text="$t('noEEBuildsYet')"
      :footer-props="{ itemsPerPageOptions: [25, 50, 100] }"
    >
      <template v-slot:item.name="{ item }"><strong>{{ item.name }}</strong></template>
      <template v-slot:item.last_image_tag="{ item }">
        <code v-if="item.last_image_tag">{{ shortenImage(item) }}</code>
        <span v-else class="text--disabled">—</span>
      </template>
      <template v-slot:item.last_build_status="{ item }">
        <v-chip
          v-if="item.last_build_status"
          :color="statusColor(item.last_build_status)"
          small
          label
        >
          {{ item.last_build_status }}
        </v-chip>
        <span v-else class="text--disabled">—</span>
      </template>
      <template v-slot:item.last_build_at="{ item }">
        <span v-if="item.last_build_at">{{ item.last_build_at | formatDate }}</span>
        <span v-else class="text--disabled">—</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <div style="white-space: nowrap">
          <v-btn icon class="mr-1" @click="runBuild(item)" :loading="runningId === item.id">
            <ZpIcon name="play" :size="18" />
          </v-btn>
          <v-btn icon class="mr-1" @click="openLog(item)" :disabled="!item.last_build_log">
            <ZpIcon name="file-text" :size="18" />
          </v-btn>
          <v-btn icon class="mr-1" @click="editItem(item.id)">
            <ZpIcon name="edit-2" :size="18" />
          </v-btn>
          <v-btn icon class="mr-1" @click="askDeleteItem(item.id)">
            <ZpIcon name="trash-2" :size="18" />
          </v-btn>
        </div>
      </template>
    </v-data-table>
  </div>
</template>
<script>
import axios from 'axios';
import socket from '@/socket';
import ItemListPageBase from '@/components/ItemListPageBase';
import EditDialog from '@/components/EditDialog.vue';
import YesNoDialog from '@/components/YesNoDialog.vue';
import EEBuildForm from '@/components/EEBuildForm.vue';

export default {
  components: { EditDialog, YesNoDialog, EEBuildForm },
  mixins: [ItemListPageBase],
  data() {
    return { runningId: null, logDialog: false, logText: '' };
  },
  mounted() {
    this.$_wsListenerId = socket.addListener((data) => this.onWebsocketDataReceived(data));
    socket.start();
  },
  beforeDestroy() {
    if (this.$_wsListenerId) socket.removeListener(this.$_wsListenerId);
  },
  methods: {
    // Server emits this on every status flip in services/eebuild/builder.go.
    // Patch the row in place if we already have it; otherwise reload so a
    // freshly-added build (from another browser tab) shows up.
    async onWebsocketDataReceived(data) {
      if (data == null || data.type !== 'ee_build_update') return;
      if (data.project_id !== this.projectId) return;
      const row = (this.items || []).find((it) => it.id === data.ee_build_id);
      if (!row) {
        await this.loadItems();
        return;
      }
      const patch = { last_build_status: data.status, last_build_at: data.last_build_at };
      if (data.image_tag) patch.last_image_tag = data.image_tag;
      Object.assign(row, patch);
      // The log is only emitted as part of the final write — re-fetch on
      // terminal status so the "View log" button picks up the new output.
      if (data.status === 'success' || data.status === 'error') {
        this.loadItems().catch(() => {});
      }
    },
    isItemPending(item) {
      return item && item.last_build_status === 'running';
    },
    getHeaders() {
      return [
        { text: this.$i18n.t('name'), value: 'name' },
        { text: this.$i18n.t('eeBuildLastImage'), value: 'last_image_tag', sortable: false },
        {
          text: this.$i18n.t('status'), value: 'last_build_status', sortable: false, width: 120,
        },
        {
          text: this.$i18n.t('eeBuildLastBuiltAt'), value: 'last_build_at', sortable: false, width: 200,
        },
        {
          text: '', value: 'actions', sortable: false, width: 170,
        },
      ];
    },
    getItemsUrl() {
      return `/api/project/${this.projectId}/ee_builds`;
    },
    getSingleItemUrl() {
      return `/api/project/${this.projectId}/ee_builds/${this.itemId}`;
    },
    getEventName() {
      return 'i-ee-build';
    },
    statusColor(s) {
      return { success: 'success', error: 'error', running: 'info' }[s] || 'default';
    },
    shortenImage(item) {
      const tag = item.last_image_tag || '';
      // Always keep the tag (after last :) visible.
      const colon = tag.lastIndexOf(':');
      if (colon < 0) return tag;
      const repo = tag.slice(0, colon);
      const ver = tag.slice(colon);
      if (repo.length <= 40) return tag;
      return `…${repo.slice(repo.length - 36)}${ver}`;
    },
    openLog(item) {
      this.logText = item.last_build_log || '';
      this.logDialog = true;
    },
    async runBuild(item) {
      this.runningId = item.id;
      try {
        await axios.post(`/api/project/${this.projectId}/ee_builds/${item.id}/run`);
        this.$emit('snackbar', this.$t('eeBuildStarted'));
      } catch (e) {
        this.$emit('snackbar', e.response?.data?.error || e.message);
      } finally {
        this.runningId = null;
        // Backend flips the status to "running" and broadcasts via WS, so the
        // table reacts on its own. Refresh now to cover the case where WS is
        // disconnected (loadItems is also called by smart polling).
        this.loadItems().catch(() => {});
      }
    },
  },
};
</script>
